import { captureMessage } from '@sentry/react';
import { CONVERSATION_CONSTANTS } from './constants';
import type {
  AttachmentsStaticContentConversationEntry,
  BaseConversationEntry,
  Content,
  ParsedEntryPayload,
  ParsedParticipantChangedEntryPayload,
  RichLinkStaticContentConversationEntry,
  ServerSentEvent,
  StaticContentConversationEntry,
  TextStaticContentConversationEntry,
} from './types';
import ApiService from 'services/ApiService';
import { getErrorMessage } from 'utils/utils';

/**
 * Parses JSON data from a server-sent event.
 * @param {object} event - Server-sent event.
 * @returns {object} - Parsed server-sent event data.
 * @throws {Error} if event data is invalid.
 */
export function parseServerSentEventData(event: MessageEvent) {
  if (
    event &&
    'data' in event &&
    event.data &&
    typeof event.data === 'string'
  ) {
    const data = JSON.parse(event.data) as ServerSentEvent;

    if (!data || typeof data !== 'object') {
      throw new Error('Error parsing data in server sent event.');
    } else {
      return data;
    }
  } else {
    throw new Error('Invalid data in server sent event.');
  }
}

/**
 * Get the sender's display name from incoming typing started/stopped indicator events.
 * @param {Object} data - Data from typing indicator server-sent events.
 * @returns {String} - Parsed display name of sender.
 */
export function getSenderDisplayName(data: ServerSentEvent) {
  return data?.conversationEntry?.senderDisplayName || '';
}

/**
 * Get the sender's role from incoming typing started/stopped indicator events.
 * @param {Object} data - Data from typing indicator server-sent events.
 * @returns {String} - Parsed role of the sender.
 */
export function getSenderRole(data: ServerSentEvent) {
  return data?.conversationEntry?.sender?.role || '';
}

/**
 * Parses JSON entry payload field from a server-sent event data.
 * @param {object} data - Server-sent event.
 * @returns {object} - Parsed server-sent event data.
 * @throws {Error} if event data is invalid.
 */
export function createConversationEntry(
  data: ServerSentEvent,
  ftConversationId: string,
  integrationId: string,
) {
  try {
    if (typeof data === 'object') {
      const entryPayload = JSON.parse(
        data.conversationEntry.entryPayload,
      ) as ParsedEntryPayload;

      // Do not create a conversation-entry for unknown/unsupported entryType.
      if (
        !Object.values(CONVERSATION_CONSTANTS.EntryTypes).includes(
          entryPayload.entryType,
        )
      ) {
        captureMessage('Unsupported entryType for Salesforce Live Chat', {
          extra: {
            conversationId: ftConversationId,
            integrationId: integrationId,
            message: `Unexpected and/or unsupported entryType: ${entryPayload.entryType}`,
            origin: ApiService.headers['solve-origin'],
          },
          level: 'info',
        });
        return;
      }

      return {
        ...({
          actorName: data.conversationEntry.senderDisplayName
            ? data.conversationEntry.senderDisplayName ||
              data.conversationEntry.sender.role
            : ('entries' in entryPayload &&
                (entryPayload.entries[0]?.displayName ||
                  entryPayload.entries[0]?.participant.role)) ||
              'Agent',
          actorType: data.conversationEntry.sender.role,
          content:
            ('abstractMessage' in entryPayload &&
              entryPayload.abstractMessage) ||
            entryPayload,
          conversationId: data.conversationId,
          entryType: entryPayload.entryType,
          messageId: data.conversationEntry.identifier,
          messageReason:
            ('messageReason' in entryPayload && entryPayload.messageReason) ||
            '',
          messageType:
            'abstractMessage' in entryPayload && entryPayload.abstractMessage
              ? entryPayload.abstractMessage.messageType
              : ('routingType' in entryPayload && entryPayload.routingType) ||
                ('entries' in entryPayload &&
                  entryPayload.entries[0]?.operation),
          sender: data.conversationEntry.sender,
          transcriptedTimestamp: data.conversationEntry.transcriptedTimestamp,
        } as const),
        deliveryAcknowledgementTimestamp: 0,
        isDelivered: false,
        isEndUserMessage: false,
        isRead: false,
        isSent: false,
        readAcknowledgementTimestamp: 0,
      };
    } else {
      throw new Error(
        `Expected an object to create a new conversation entry but instead, received ${data}`,
      );
    }
  } catch (err) {
    throw new Error(
      `Something went wrong while creating a conversation entry: ${getErrorMessage(err)}`,
    );
  }
}

export type AppConversationEntry = NonNullable<
  ReturnType<typeof createConversationEntry>
>;

//============================================================== STATIC TEXT MESSAGE functions ==============================================================
/**
 * Validates whether the supplied object is a conversation-entry with entry type as CONVERSATION_MESSAGE.
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the conversation-entry is a CONVERSATION_MESSAGE and FALSE - otherwise.
 */
function isConversationEntryMessage(
  conversationEntry: BaseConversationEntry | AppConversationEntry,
) {
  if (conversationEntry) {
    return (
      conversationEntry.entryType ===
      CONVERSATION_CONSTANTS.EntryTypes.CONVERSATION_MESSAGE
    );
  }
  return false;
}

/**
 * Validates whether the supplied CONVERSATION_MESSAGE is originating from an end-user participant.
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the CONVERSATION_MESSAGE is sent by the end-user participant and FALSE - otherwise.
 */
export function isMessageFromEndUser(
  conversationEntry: BaseConversationEntry | AppConversationEntry,
) {
  if (isConversationEntryMessage(conversationEntry)) {
    return (
      conversationEntry.actorType ===
      CONVERSATION_CONSTANTS.ParticipantRoles.ENDUSER
    );
  }
  return false;
}

/**
 * Validates whether the supplied CONVERSATION_MESSAGE is a STATIC_CONTENT_MESSAGE (i.e. messageType === "STATIC_CONTENT_MESSAGE").
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the CONVERSATION_MESSAGE is a STATIC_CONTENT_MESSAGE and FALSE - otherwise.
 */
function isConversationEntryStaticContentMessage(
  conversationEntry: BaseConversationEntry | AppConversationEntry,
): conversationEntry is StaticContentConversationEntry {
  if (isConversationEntryMessage(conversationEntry)) {
    const content = conversationEntry.content as Content;

    return (
      content?.messageType ===
      CONVERSATION_CONSTANTS.MessageTypes.STATIC_CONTENT_MESSAGE
    );
  }
  return false;
}

/**
 * Gets the supplied STATIC_CONTENT_MESSAGE's payload.
 * @param {object} conversationEntry
 * @returns {object|undefined}
 */
function getStaticContentPayload(
  conversationEntry: BaseConversationEntry | AppConversationEntry,
) {
  if (isConversationEntryStaticContentMessage(conversationEntry)) {
    return conversationEntry.content?.staticContent;
  }
  return undefined;
}

/**
 * Validates whether the supplied STATIC_CONTENT_MESSAGE is a Text Message (i.e. formatType === "Text").
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the STATIC_CONTENT_MESSAGE is a Text Message and FALSE - otherwise.
 */
export function isTextMessage(
  conversationEntry: BaseConversationEntry | AppConversationEntry,
): conversationEntry is TextStaticContentConversationEntry {
  if (isConversationEntryStaticContentMessage(conversationEntry)) {
    return (
      getStaticContentPayload(conversationEntry)?.formatType ===
      CONVERSATION_CONSTANTS.FormatTypes.TEXT
    );
  }

  return false;
}

export function isAttachmentsMessage(
  conversationEntry: BaseConversationEntry | AppConversationEntry,
): conversationEntry is AttachmentsStaticContentConversationEntry {
  if (isConversationEntryStaticContentMessage(conversationEntry)) {
    return (
      getStaticContentPayload(conversationEntry)?.formatType ===
      CONVERSATION_CONSTANTS.FormatTypes.ATTACHMENTS
    );
  }

  return false;
}

export function isRichLinkMessage(
  conversationEntry: BaseConversationEntry | AppConversationEntry,
): conversationEntry is RichLinkStaticContentConversationEntry {
  if (isConversationEntryStaticContentMessage(conversationEntry)) {
    return (
      getStaticContentPayload(conversationEntry)?.formatType ===
      CONVERSATION_CONSTANTS.FormatTypes.RICH_LINK
    );
  }

  return false;
}

/**
 * Gets the supplied Text Message's text.
 * @param {object} conversationEntry
 * @returns {string}
 */
export function getTextMessageContent(
  conversationEntry: BaseConversationEntry,
) {
  if (isTextMessage(conversationEntry)) {
    return getStaticContentPayload(conversationEntry)?.text || '';
  }
  return '';
}

//============================================================== CHOICES MESSAGE functions ==============================================================
/**
 * Validates whether the supplied CONVERSATION_MESSAGE is a CHOICES_MESSAGE (i.e. messageType === "ChoicesMessage").
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the CONVERSATION_MESSAGE is a CHOICES_MESSAGE and FALSE - otherwise.
 */
function isConversationEntryChoicesMessage(
  conversationEntry: BaseConversationEntry | AppConversationEntry,
) {
  if (isConversationEntryMessage(conversationEntry)) {
    return (
      // @ts-expect-error Sorry, I tried
      conversationEntry.content?.messageType ===
      CONVERSATION_CONSTANTS.MessageTypes.CHOICES_MESSAGE
    );
  }
  return false;
}

/**
 * Validates whether the supplied CHOICES_MESSAGE is QUICK_REPLIES (i.e. formatType === "QuickReplies") or BUTTONS (i.e. formatType === "Buttons").
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the CHOICES_MESSAGE is a QUICK_REPLIES or BUTTONS format type and FALSE - otherwise.
 */
export function isChoicesMessage(
  conversationEntry: BaseConversationEntry | AppConversationEntry,
) {
  if (isConversationEntryChoicesMessage(conversationEntry)) {
    return (
      // @ts-expect-error Sorry, I tried
      conversationEntry.content.choices?.formatType ===
        CONVERSATION_CONSTANTS.FormatTypes.QUICK_REPLIES ||
      // @ts-expect-error Sorry, I tried
      conversationEntry.content.choices?.formatType ===
        CONVERSATION_CONSTANTS.FormatTypes.BUTTONS
    );
  }
  return false;
}

// eslint-disable-next-line unused-imports/no-unused-vars
function hasParticipantJoined(content: ParsedParticipantChangedEntryPayload) {
  return (
    content.entries[0]?.operation ===
    CONVERSATION_CONSTANTS.ParticipantChangedOperations.ADD
  );
}

// eslint-disable-next-line unused-imports/no-unused-vars
function getParticipantChangeEventPartcipantName(
  content: ParsedParticipantChangedEntryPayload,
) {
  return (
    content.entries[0]?.displayName ||
    content.entries[0]?.participant.role ||
    'Agent'
  );
}
